import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Rocket, Plus, Play, RotateCcw, Search, Filter, CheckCircle, XCircle, Clock, Loader } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusIcons = {
  success: <CheckCircle className="w-5 h-5 text-accent" />,
  failed: <XCircle className="w-5 h-5 text-destructive" />,
  deploying: <Loader className="w-5 h-5 text-yellow-500 animate-spin" />,
  pending: <Clock className="w-5 h-5 text-muted-foreground" />,
  'rolled-back': <RotateCcw className="w-5 h-5 text-secondary" />,
};

export default function DeploymentsPage() {
  const { getAuthHeaders } = useAuth();
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterEnv, setFilterEnv] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newDeployment, setNewDeployment] = useState({
    name: '',
    branch: 'main',
    environment: 'staging',
    description: ''
  });

  const fetchDeployments = async () => {
    try {
      const response = await axios.get(`${API_URL}/deployments`, {
        headers: getAuthHeaders()
      });
      setDeployments(response.data);
    } catch (error) {
      console.error('Failed to fetch deployments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeployments();
  }, [getAuthHeaders]);

  const handleCreateDeployment = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/deployments`, newDeployment, {
        headers: getAuthHeaders()
      });
      setDeployments([response.data, ...deployments]);
      setCreateDialogOpen(false);
      setNewDeployment({ name: '', branch: 'main', environment: 'staging', description: '' });
      toast.success('Deployment created successfully!');
    } catch (error) {
      toast.error('Failed to create deployment');
    }
  };

  const handleDeploy = async (deploymentId) => {
    try {
      await axios.post(`${API_URL}/deployments/${deploymentId}/deploy`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Deployment started!');
      fetchDeployments();
    } catch (error) {
      toast.error('Failed to start deployment');
    }
  };

  const handleRollback = async (deploymentId) => {
    try {
      await axios.post(`${API_URL}/deployments/${deploymentId}/rollback`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Rollback initiated!');
      fetchDeployments();
    } catch (error) {
      toast.error('Failed to rollback deployment');
    }
  };

  const filteredDeployments = deployments.filter(d => {
    const matchesSearch = d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         d.branch.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesEnv = filterEnv === 'all' || d.environment === filterEnv;
    return matchesSearch && matchesEnv;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="font-heading text-3xl font-bold text-white" data-testid="deployments-title">
              Deployments
            </h1>
            <p className="text-foreground/60">Manage and monitor your deployments</p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-black font-bold hover:shadow-neon-cyan" data-testid="create-deployment-btn">
                <Plus className="w-5 h-5 mr-2" />
                New Deployment
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border" aria-describedby="create-deployment-description">
              <DialogHeader>
                <DialogTitle className="text-white font-heading">Create New Deployment</DialogTitle>
                <p id="create-deployment-description" className="sr-only">Form to create a new deployment with name, branch, environment, and description fields.</p>
              </DialogHeader>
              <form onSubmit={handleCreateDeployment} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground/80">Deployment Name</Label>
                  <Input
                    value={newDeployment.name}
                    onChange={(e) => setNewDeployment({ ...newDeployment, name: e.target.value })}
                    placeholder="e.g., Release v2.4.0"
                    className="bg-background border-border"
                    required
                    data-testid="deployment-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Branch</Label>
                  <Input
                    value={newDeployment.branch}
                    onChange={(e) => setNewDeployment({ ...newDeployment, branch: e.target.value })}
                    placeholder="main"
                    className="bg-background border-border"
                    required
                    data-testid="deployment-branch-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Environment</Label>
                  <Select
                    value={newDeployment.environment}
                    onValueChange={(value) => setNewDeployment({ ...newDeployment, environment: value })}
                  >
                    <SelectTrigger className="bg-background border-border" data-testid="deployment-env-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dev">Development</SelectItem>
                      <SelectItem value="staging">Staging</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground/80">Description (Optional)</Label>
                  <Input
                    value={newDeployment.description}
                    onChange={(e) => setNewDeployment({ ...newDeployment, description: e.target.value })}
                    placeholder="Brief description of changes"
                    className="bg-background border-border"
                    data-testid="deployment-description-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-primary text-black font-bold" data-testid="create-deployment-submit">
                  Create Deployment
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search deployments..."
              className="pl-10 bg-card border-border"
              data-testid="deployments-search"
            />
          </div>
          <Select value={filterEnv} onValueChange={setFilterEnv}>
            <SelectTrigger className="w-full sm:w-48 bg-card border-border">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Environment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Environments</SelectItem>
              <SelectItem value="dev">Development</SelectItem>
              <SelectItem value="staging">Staging</SelectItem>
              <SelectItem value="production">Production</SelectItem>
            </SelectContent>
          </Select>
        </motion.div>

        {/* Deployments List */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredDeployments.length === 0 ? (
          <Card className="bg-card/50 border-border">
            <CardContent className="py-16 text-center">
              <Rocket className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-heading font-semibold text-white mb-2">No Deployments Yet</h3>
              <p className="text-muted-foreground mb-6">Create your first deployment to get started</p>
              <Button onClick={() => setCreateDialogOpen(true)} className="bg-primary text-black font-bold">
                <Plus className="w-5 h-5 mr-2" />
                Create Deployment
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredDeployments.map((deployment, i) => (
              <motion.div
                key={deployment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <Card className="bg-card/50 border-border hover:border-primary/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-primary/10 rounded-lg">
                          {statusIcons[deployment.status] || statusIcons.pending}
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-white text-lg">{deployment.name}</h3>
                          <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <span className="text-primary">{deployment.branch}</span>
                              <span>→</span>
                              <span className="capitalize">{deployment.environment}</span>
                            </span>
                            <span>•</span>
                            <span className="font-code text-xs">{deployment.commit_hash}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2">
                            {new Date(deployment.created_at).toLocaleString()}
                            {deployment.duration && ` • ${deployment.duration}s`}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                          deployment.status === 'success' ? 'bg-accent/20 text-accent' :
                          deployment.status === 'failed' ? 'bg-destructive/20 text-destructive' :
                          deployment.status === 'deploying' ? 'bg-yellow-500/20 text-yellow-500' :
                          deployment.status === 'rolled-back' ? 'bg-secondary/20 text-secondary' :
                          'bg-muted text-muted-foreground'
                        }`}>
                          {deployment.status}
                        </span>
                        
                        {deployment.status === 'pending' && (
                          <Button 
                            size="sm" 
                            onClick={() => handleDeploy(deployment.id)}
                            className="bg-accent text-black font-bold hover:shadow-neon-green"
                            data-testid={`deploy-btn-${deployment.id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Deploy
                          </Button>
                        )}
                        
                        {deployment.status === 'success' && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleRollback(deployment.id)}
                            className="border-secondary text-secondary hover:bg-secondary/10"
                            data-testid={`rollback-btn-${deployment.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Rollback
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
